import i18n from 'i18next';
import { socialDatasetId } from './Query';
import { defaultQueryFieldTypeOrder } from './QueryUtility';

export enum QueryFieldTypes {
  keyword = "keyword",
  keywords = "keywords",
  timestamp = "timestamp",
  integer = "integer",
  decimal = "decimal",
  text = "text",
  boolean = "boolean",
  latitude_longitude = "latitude_longitude",
  uid = "uid",
}

export enum QueryFieldFilterTypes {
  record = "record",
}

export enum SocialFieldCategories {
  TEXT = "text",
  DATETIME = "date / time",
  SOURCE = "source",
  SOCIAL = "social metrics",
  INFEGY_IQ = "infegy IQ",
  GEOGRAPHY = "geography",
  DEMOGRAPHICS = "demographics",
  STATS = "stats",
  OTHER = "other"
}

export const recordsField: QueryField = {
  id: "__count__",
  label: "records",
  type: QueryFieldTypes.integer,
  agg_supported: true,
  query_supported: false,
  _social_field_category: SocialFieldCategories.STATS,
};

export const getRecordsField = () => {
  return {
    id: "__count__",
    label: i18n.t("records"),
    type: QueryFieldTypes.integer,
    agg_supported: true,
    query_supported: false,
    _social_field_category: SocialFieldCategories.STATS,
  };
};

export const socialUniverseField: QueryField = {
  id: "social_universe",
  label: i18n.t("social_universe"),
  type: QueryFieldTypes.integer,
  agg_supported: true,
  query_supported: false,
  _social_field_category: SocialFieldCategories.STATS,
};

export const getSocialUniverseField = () => {
  return {
    id: "social_universe",
    label: i18n.t("social_universe"),
    type: QueryFieldTypes.integer,
    agg_supported: true,
    query_supported: false,
    _social_field_category: SocialFieldCategories.STATS,
  };
};

export interface QueryFieldOption {
  id: string | null;
  label: string;
  // Is a suggestion option from the API
  _is_suggestion?: boolean;
}

export interface QueryField {
  agg_supported: boolean;
  colors?: any;
  data_type?: string;
  id: string;
  index_precision?: number;
  label: string;
  max?: string | number;
  min?: string | number;
  options?: QueryFieldOption[];
  suggestions?: string[];
  query_supported: boolean;
  resolution?: string;
  type: QueryFieldTypes;
  store_precision?: number;
  _social_field_category?: SocialFieldCategories;
}

export interface QueryInfoDataset {
  id: string;
  name: string;
}


export const socialFieldCategoryMap = {
  // Text
  "body": SocialFieldCategories.TEXT,
  "title": SocialFieldCategories.TEXT,
  "description": SocialFieldCategories.TEXT,
  "media_title": SocialFieldCategories.TEXT,
  "bio": SocialFieldCategories.TEXT,

  // Time
  "published": SocialFieldCategories.DATETIME,
  "fetched": SocialFieldCategories.DATETIME,

  // Source
  "link": SocialFieldCategories.SOURCE,
  "author": SocialFieldCategories.SOURCE,
  "handle": SocialFieldCategories.SOURCE,
  "site": SocialFieldCategories.SOURCE,

  // Geography
  "geohash": SocialFieldCategories.GEOGRAPHY,
  "country": SocialFieldCategories.GEOGRAPHY,
  "ca_province": SocialFieldCategories.GEOGRAPHY,
  "us_state": SocialFieldCategories.GEOGRAPHY,
  "us_dma": SocialFieldCategories.GEOGRAPHY,

  // Social
  "likes": SocialFieldCategories.SOCIAL,
  "shares": SocialFieldCategories.SOCIAL,
  "comments": SocialFieldCategories.SOCIAL,
  "views": SocialFieldCategories.SOCIAL,
  "total_engagements": SocialFieldCategories.SOCIAL,
  "is_reply_or_share": SocialFieldCategories.SOCIAL,
  "influence": SocialFieldCategories.SOCIAL,
  "followers": SocialFieldCategories.SOCIAL,
  "following": SocialFieldCategories.SOCIAL,

  // Linguistics
  "sentiment": SocialFieldCategories.INFEGY_IQ,
  "themes": SocialFieldCategories.INFEGY_IQ,
  "emotions": SocialFieldCategories.INFEGY_IQ,
  "taxonomies": SocialFieldCategories.INFEGY_IQ,
  "entities": SocialFieldCategories.INFEGY_IQ,

  // Demographics
  "source_age": SocialFieldCategories.DEMOGRAPHICS,
  "gender": SocialFieldCategories.DEMOGRAPHICS,
  "source_interests": SocialFieldCategories.DEMOGRAPHICS,
  "language": SocialFieldCategories.DEMOGRAPHICS,

  // Other
  "links_to": SocialFieldCategories.OTHER,
  "rand": SocialFieldCategories.OTHER,
  "channels": SocialFieldCategories.OTHER,
  "audience_segment": SocialFieldCategories.OTHER,
  "source": SocialFieldCategories.OTHER,
};


export class QueryInfo {
  dataset: QueryInfoDataset = {id: "", name: ""};
  fields: QueryField[] = [];
  supported_aggregation_ops: Array<string> = [];
  query_hash?: string;

  constructor(queryInfo: any) {
    this.fromJSON(queryInfo);
    return this;
  }

  fromJSON(queryInfo: any) {
    this.dataset = queryInfo?.dataset as QueryInfoDataset;
    this.supported_aggregation_ops = queryInfo?.supported_aggregation_ops;
    this.query_hash = queryInfo.query_hash;

    let fields: Array<QueryField> = queryInfo?.fields ?? [];
    const translatedRecordsField = {...recordsField, label: i18n.t("records")};
    fields.push(translatedRecordsField);
    if (this.supported_aggregation_ops?.includes("social_universe")) {
      const translatedSocialUniverse = {...socialUniverseField, label: i18n.t("social_universe")};
      fields.push(translatedSocialUniverse);
    }

    if (this.dataset?.id === socialDatasetId) {
      // add social field categories to the fields
      fields = fields.map((field: QueryField) => {
        const fieldCategory = socialFieldCategoryMap[field.id as keyof typeof socialFieldCategoryMap];
        if (fieldCategory) {
          field._social_field_category = fieldCategory;
        }
        return field;
      });
    }

    // Pre-sort the fields' options by their label so they can be used more readily.
    this.fields = fields?.map((field: QueryField) => {
      const sortedOptions = field.options?.sort((a, b) => {
        if (a.label > b.label) return 1;
        if (a.label < b.label) return -1;
        return 0;
      });
      field.options = sortedOptions;
      return field;
    });
  }
}

export function fieldIsKeyword(field: QueryField) : boolean {
  return field.type === QueryFieldTypes.keyword || field.type === QueryFieldTypes.keywords || field.type == QueryFieldTypes.uid;
}

export function fieldIsRecordType(field: QueryField) : boolean {
  return field.id === recordsField.id || field.id === socialUniverseField.id;
}

export function fieldMatchesAllowedType(field: QueryField, allowedFieldType: string) : boolean {
  if (allowedFieldType === QueryFieldFilterTypes.record) {
    return fieldIsRecordType(field);
  }

  return field.type === allowedFieldType;
}

export enum KeywordInputType {
  options,
  autocomplete,
  string,
  numeric
}

export function getKeywordInputType(field: QueryField) : KeywordInputType {
  if (field.data_type === "integer" || field.data_type === "decimal") {
    return KeywordInputType.numeric;
  } else if (field.options) {
    return KeywordInputType.options;
  } else if (field.agg_supported) {
    return KeywordInputType.autocomplete;
  } else {
    return KeywordInputType.string;
  }
}

export function getFieldById(queryInfo: QueryInfo, id: string) : QueryField | undefined {
  return queryInfo.fields.find((field) => field.id === id);
}

export function getFieldLabelById(queryInfo: QueryInfo, id: string) : string {
  const field = getFieldById(queryInfo, id);
  return field ? field.label : "";
}

export interface QueryFieldFilters {
  allowedFieldTypes?: string[];
  requireQuerySupported?: boolean;
  requireAggSupported?: boolean;
  searchString?: string;
}

export function filterQueryFields(queryFields: QueryField[], fieldFilters: QueryFieldFilters) : QueryField[] {
  const { allowedFieldTypes, requireQuerySupported, requireAggSupported, searchString } = fieldFilters;
  return (queryFields || []).filter((field) => {
    // Remove latlong field from the allowedFieldTypes temporarily, as it is not supported in the UI
    if (field.type === "latitude_longitude") {
      return false;
    }
    // Remove fields that are not in the allowedFieldTypes
    if (allowedFieldTypes && allowedFieldTypes?.length && !allowedFieldTypes.some((allowedFieldType) => fieldMatchesAllowedType(field, allowedFieldType))) {
      return false;
    }
    // Remove fields that are not query supported, if required
    if (requireQuerySupported && !field.query_supported) {
      return false;
    }
    // Remove fields that are not agg supported, if required
    if (requireAggSupported && !field.agg_supported) {
      return false;
    }
    // Remove fields that do not match the search string
    const localSearchString = searchString?.trim().toLowerCase();
    if (localSearchString && !field.label.toLowerCase().includes(localSearchString)) {
      return false;
    }
    return true;
  });
}

export function sortFieldsByTypeAndLabel(queryFields: QueryField[], typeOrder?: string[]) : QueryField[] {
  const innerTypeOrder = typeOrder || defaultQueryFieldTypeOrder;

  return (queryFields || []).sort((a, b) => {
    return a.label.localeCompare(b.label);
  }).sort((a, b) => {
    // Sort by typeOrder if provided
    if (innerTypeOrder.length > 0) {
      // First sort by _social_field_category if it exists
      if (a._social_field_category && b._social_field_category && a._social_field_category !== b._social_field_category) {
        return innerTypeOrder.indexOf(a._social_field_category) - innerTypeOrder.indexOf(b._social_field_category);
      // Then sort by type if it exists
      } else if (a.type !== b.type) {
        const aTypeOrder = innerTypeOrder.indexOf(a.type);
        const bTypeOrder = innerTypeOrder.indexOf(b.type);
        // If we have typeOrder for both, sort by that
        if (aTypeOrder !== -1 && bTypeOrder !== -1) {
          return aTypeOrder - bTypeOrder;
        // Otherwise, sort type alphabetically
        } else {
          return a.type.localeCompare(b.type);
        }
      }
    } else {
      return 0;
    }

    return 0;
  }) || [];
}